<template>
  <fragment>
    <div class="row">
      <div class="col-12 col-lg-6">
        <div class="form-group">
          <label for="episode_author"><strong>Author/Presenter <span id="episode_author_tooltip" class="ml-2 la la-question-circle" /></strong></label>
          <b-tooltip target="episode_author_tooltip" triggers="hover">
            <div v-sanitise="tooltips.episode_author_tooltip" />
          </b-tooltip>
          <input id="episode_author"
                 v-model="episode.episode_detail.author"
                 type="text"
                 class="form-control"
                 placeholder="John Doe">
        </div>
      </div>
      <div class="col-12 col-lg-6">
        <div class="form-group">
          <label><strong>Homepage URL <span id="episode_website_tooltip" class="ml-2 la la-question-circle" /></strong></label>
          <b-tooltip target="episode_website_tooltip" triggers="hover">
            <div v-sanitise="tooltips.episode_website_tooltip" />
          </b-tooltip>
          <input v-model="episode.episode_detail.link"
                 v-validate="'url'"
                 data-vv-scope="episode-advanced-form"
                 name="Homepage URL"
                 type="text"
                 class="form-control"
                 placeholder="https://my-podcast.com">
          <p v-show="errors.has('Homepage URL', 'episode-advanced-form')" class="help text-ss-danger">
            Homepage URL is not valid. Please include 'http://' or 'https://' prefix.
          </p>
        </div>
      </div>
      <div class="col-12 col-lg-6">
        <div class="form-group">
          <label for="episode_keywords"><strong>Tags <span id="tags_tooltip" class="ml-2 la la-question-circle" /></strong></label>
          <b-tooltip target="tags_tooltip" triggers="hover">
            <div v-sanitise="tooltips.tags_tooltip" />
          </b-tooltip>
          <active-passive :data-val="ms_tag_values" bordered>
            <template #ap-passive>
              <multi-select-tag-bag :tags-list="ms_tag_values" :limit="20" :show-controls="false" />
            </template>
            <template #ap-active>
              <multiselect v-model="ms_tag_values"
                           :options="auto_complete_tags"
                           :multiple="true"
                           :taggable="true"
                           placeholder="Search or add tags..."
                           @tag="addTag" />
            </template>
          </active-passive>
        </div>
      </div>
      <div class="col-12 col-lg-6">
        <div class="form-group">
          <label>
            <strong>Downloadable</strong>
          </label>
          <div>
            <span class="kt-switch kt-switch--icon">
              <label>
                <input v-model="episode.downloadable"
                       type="checkbox"
                       :checked="episode.downloadable"
                       name="Published">
                <span />
              </label>
            </span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="episode.uuid && chapters_enabled" class="row">
      <div class="col-12 col-lg-6">
        <div class="form-group">
          <label><strong>Chapters Markers <span id="chapter_markers_tooltip" class="ml-2 la la-question-circle" /></strong></label>
          <b-tooltip target="chapter_markers_tooltip" triggers="hover">
            <div v-sanitise="tooltips.chapter_markers_tooltip" />
          </b-tooltip>
          <b-table v-if="chapter_markers && chapter_markers.length" :fields="chapter_marker_fields" :items="chapter_markers" />
          <hr v-else>
        </div>
        <div v-if="chapter_markers && chapter_markers.length" class="w-100">
          <a :href="`/chapter-markers/${episode.uuid}/edit`" target="_blank" class="btn btn-ss">Manage Chapter Markers</a>
        </div>
        <div v-else>
          <p>This {{ getItemLabel(true) }} doesn't have any Chapter Markers</p>
          <a :href="`/chapter-markers/${episode.uuid}/edit`" class="btn btn-ss btn-icon-sm">
            <i class="la la-plus" /> Chapter Markers
          </a>
        </div>
      </div>
      <div v-if="ads_enabled" class="col-12 col-lg-6">
        <div class="form-group">
          <label><strong>Ad Markers <span id="ad_markers_tooltip" class="ml-2 la la-question-circle" /></strong></label>
          <b-tooltip target="ad_markers_tooltip" triggers="hover">
            <div v-sanitise="tooltips.ad_markers_tooltip" />
          </b-tooltip>
          <b-table v-if="ad_markers && ad_markers.length" :fields="chapter_marker_fields" :items="ad_markers" />
          <hr v-else>
        </div>
        <div v-if="ad_markers && ad_markers.length" class="w-100">
          <a :href="`/chapter-markers/${episode.uuid}/edit`" target="_blank" class="btn btn-ss">Manage Ad Markers</a>
        </div>
        <div v-else>
          <p>This {{ getItemLabel(true) }} doesn't have any Ad Markers</p>
          <a :href="`/chapter-markers/${episode.uuid}/edit`" class="btn btn-ss btn-icon-sm">
            <i class="la la-plus" /> Ad Markers
          </a>
        </div>
      </div>
    </div>
  </fragment>
</template>

<script>
import { useEpisodeManageStore } from "VueStore/app/episodeManage";
import PodcastValidateMixin from "VueMixins/PodcastValidateMixin";
import { convertToHhmmss } from "Helpers/formatters";
import { mapWritableState, mapActions } from 'pinia';
import TooltipsMixin from "VueMixins/TooltipsMixin";
import ActivePassive from 'VueComponents/ActivePassive';
import MultiSelectTagBag from "VueComponents/MultiSelectTagBag";

export default {
  components: {
    ActivePassive,
    MultiSelectTagBag,
  },
  mixins: [PodcastValidateMixin, TooltipsMixin],
  data() {
    return {
      chapters_enabled: true,
      ads_enabled: false,
      chapter_markers: [],
      ad_markers: [],
      chapter_marker_fields: [
        'name',
        {
          key: 'time',
          formatter: value => convertToHhmmss(value),
        },
      ],
    };
  },
  computed: {
    ...mapWritableState(useEpisodeManageStore, [
      'episode',
      'ms_tag_values',
      'auto_complete_tags',
    ]),
  },
  beforeMount() {
    this.chapter_markers = _.filter(
      this.episode.chapter_markers,
      { is_ad: 0 },
    );
    this.ad_markers = _.filter(
      this.episode.chapter_markers,
      { is_ad: 1 },
    );

    _.forEach(this.ad_markers, (ad) => {
      ad.name = "AD_START";
    });

    this.ads_enabled = window.user?.customer?.ads_enabled;
    this.chapters_enabled = this.permissionsCanAccessFeature('chapters');
  },
  mounted() {
    this.getAllTags();
  },
  methods: {
    ...mapActions(useEpisodeManageStore, [
      'addTag',
      'getAllTags',
    ]),
  },
};
</script>
